import { IdleTimerManagerOptions } from './types';
import CookiesUtil from './utils/cookies-util';
import EventsUtil from './utils/events-util';
import LoggingUtil, {
  DEFAULT_LOG_LEVEL,
  LOG_LEVELS,
  LogLevel,
} from './utils/logging-util';
import TimerUtil from './utils/timer-util';

const DEBOUNCE_OPTIONS = {
  delay: 10_000,
  leading: true,
  maxWait: 15_000,
} as const;
const DEFAULT_ACTIVITY_EVENTS = [
  'keydown',
  'mousedown',
  'mousemove',
  'mouseover',
  'touchstart',
  'wheel',
] as const;
const DEFAULT_COOKIE_KEY = '__ACC_LAST_ACTIVE__' as const;
const DEFAULT_TIMEOUT_MS = 7_200_000 as const; // 2 hours
const INTERVAL_MIN = 5_000; // 5 seconds
const INTERVAL_THRESHOLD = 60_000; // 1 minute
const LOG_LEVEL_STORAGE_KEY = 'idle-timer-log-level';
const LOGGER_CONTEXT = 'IdleTimer';

class IdleTimerManager {
  private cookieKey: string;
  private eventsManager: EventsUtil;
  private logger: LoggingUtil;
  private onIdle: () => void;
  private started: boolean = false;
  private timeoutMs: number;
  private timer: TimerUtil;

  // ----- Constructor -----
  constructor({
    activityEvents = DEFAULT_ACTIVITY_EVENTS,
    cookieKey = DEFAULT_COOKIE_KEY,
    onIdle = () => {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }: IdleTimerManagerOptions = {}) {
    this.cookieKey = cookieKey;
    this.onIdle = onIdle;
    this.timeoutMs = timeoutMs;

    this.logger = this.initLogger();

    this.eventsManager = this.initEventsManager(activityEvents, this.logger);
    this.timer = this.initTimer(this.logger);

    this.init(this.logger);
  }

  // ----- Public Methods -----
  start(): void {
    if (this.started) {
      this.logger.log(
        'info',
        'IdleTimerManager.start() called again; no duplicates.',
      );
      return;
    }

    if (this.isIdle()) {
      this.handleIdle(); // new tab or logged out manually
      return;
    }

    this.started = true;

    this.logger.log(
      'info',
      `Starting IdleTimerManager with timeoutMs=${this.timeoutMs}.`,
    );

    this.timer.start();
    this.eventsManager.bind();
  }

  stop(): void {
    if (!this.started) {
      this.logger.log(
        'info',
        'IdleTimerManager.stop() called but manager not started.',
      );
      return;
    }
    this.logger.log('info', 'Stopping IdleTimerManager.');
    this.eventsManager.unbind();
    this.timer.stop();
    this.started = false;
  }

  // ----- Initialization Methods (private) -----
  private init(logger: LoggingUtil): void {
    if (!CookiesUtil.get(this.cookieKey)) {
      this.updateActivity(); // new login
    }

    window.onbeforeunload = () => {
      // handle refreshes and tab closures
      if (this.started) {
        logger.log('info', 'Window unloading: stopping IdleTimerManager.');
        this.stop();
      }
    };

    logger.log('debug', 'IdleTimerManager initialized.');
  }

  private initEventsManager(
    activityEvents: readonly string[],
    logger: LoggingUtil,
  ): EventsUtil {
    const handler = () => {
      if (this.isIdle()) {
        this.handleIdle();
      } else {
        this.updateActivity();
      }
    };

    return new EventsUtil(activityEvents, handler, logger, DEBOUNCE_OPTIONS);
  }

  private initLogger(): LoggingUtil {
    let logLevel = (CookiesUtil.get(LOG_LEVEL_STORAGE_KEY) ||
      localStorage?.getItem(LOG_LEVEL_STORAGE_KEY)) as LogLevel;
    if (!logLevel || !LOG_LEVELS.includes(logLevel)) {
      logLevel = DEFAULT_LOG_LEVEL;
    }

    return new LoggingUtil(LOGGER_CONTEXT, logLevel);
  }

  private initTimer(logger: LoggingUtil): TimerUtil {
    const interval =
      this.timeoutMs > INTERVAL_THRESHOLD ? INTERVAL_THRESHOLD : INTERVAL_MIN;
    const callback = () => {
      if (this.isIdle()) {
        this.handleIdle();
      }
    };

    return new TimerUtil(callback, interval, logger);
  }

  // ----- Core Functionality Methods (private) -----
  private handleIdle(): void {
    this.logger.log(
      'info',
      `Timeout triggered after ${this.timeoutMs}ms of inactivity.`,
    );
    this.stop();
    CookiesUtil.remove(this.cookieKey);
    this.onIdle();
  }

  private isIdle(): boolean {
    const lastActive = CookiesUtil.get(this.cookieKey);
    if (!lastActive) {
      return true; // cookie removed due to idle detection in another tab
    }
    const remaining = this.timeoutMs - (Date.now() - Number(lastActive));
    this.logger.log('debug', `Remaining: ${remaining}`);

    return remaining <= 0;
  }

  private updateActivity(): void {
    CookiesUtil.set(this.cookieKey, Date.now().toString());
    this.logger.log('debug', 'Activity updated (timestamp refreshed).');
  }
}

export default IdleTimerManager;
export type { IdleTimerManagerOptions };
